import { Columns, Column, Stack, Button } from 'braid-design-system';
import debounce from 'lodash/debounce';
import { useState } from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import type { ListingProduct } from 'src/generated/graphql';

import { usePaymentSubmission } from '../../../hooks/usePaymentSubmission';

import { AcceptedCards } from './components/AcceptedCards/AcceptedCards';
import { CheckoutForm } from './components/CheckoutForm/CheckoutForm';
import { CheckoutFormErrorView } from './components/CheckoutFormErrorView/CheckoutFormErrorView';
import { PaymentHeading } from './components/PaymentHeading/PaymentHeading';
import { SearchBoostProductDetails } from './components/ProductDetails/SearchBoostProductDetails';
import { StandardProductDetails } from './components/ProductDetails/StandardProductDetails';
import { SearchBoostAddOn } from './components/SearchBoostAddOn/SearchBoostAddOn';

interface StandardPaymentScreenProps {
  standardProduct: ListingProduct;
  searchBoostProduct: ListingProduct;
  standardAndSearchBoost: ListingProduct;

  clientId: string;
  listingId: string | null;
}

export const StandardPaymentScreen = (props: StandardPaymentScreenProps) => {
  const { pay, loading, error } = usePaymentSubmission();
  const [hasSelectedSearchBoostAddon, setHasSelectedSearchBoostAddon] =
    useState(false);

  const selectedProduct = hasSelectedSearchBoostAddon
    ? props.standardAndSearchBoost
    : props.standardProduct;
  const sourceProducts = hasSelectedSearchBoostAddon
    ? [props.standardProduct, props.searchBoostProduct]
    : [props.standardProduct];

  const handleSubmit = debounce(async () => {
    await pay({
      clientId: props.clientId,
      listingId: props.listingId,
      selectedProduct,
      sourceProducts,
    });
  }, 500);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Columns space="medium" collapseBelow="desktop">
        <Column width="2/5">
          <Stack space="large">
            <PaymentHeading />

            {loading ? (
              <CustomLoader />
            ) : (
              <Stack space="medium">
                <AcceptedCards />

                <CheckoutForm />

                <SearchBoostAddOn
                  checked={hasSelectedSearchBoostAddon}
                  onChange={() =>
                    setHasSelectedSearchBoostAddon((prevState) => !prevState)
                  }
                  searchBoostProduct={props.searchBoostProduct}
                />

                <Button type="submit" tone="formAccent">
                  Pay ${selectedProduct.cost / 100} + GST
                </Button>

                {error && <CheckoutFormErrorView error={error} />}
              </Stack>
            )}
          </Stack>
        </Column>

        <Column>
          <Stack space="small">
            <StandardProductDetails product={props.standardProduct} />
            {hasSelectedSearchBoostAddon && (
              <SearchBoostProductDetails
                searchBoostProduct={props.searchBoostProduct}
              />
            )}
          </Stack>
        </Column>
      </Columns>
    </form>
  );
};
